import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import ConsultationCTA from '../components/ConsultationCTA';


const Compliance = () => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div style={{
      fontFamily: "'Poppins', sans-serif",
      backgroundColor: '#fefefe',
      minHeight: '100vh'
    }}>
      <Helmet>
        <title>HIPAA Compliance & Healthcare Data Security | MDeRCM</title>
        <meta name="description" content="MDeRCM is fully HIPAA compliant with ISO 9001 & ISO 27001 certifications. Our healthcare revenue cycle management services maintain the highest data security and compliance standards." />
        <meta name="keywords" content="HIPAA compliance medical billing, healthcare data security, ISO 27001 medical billing, HIPAA compliant RCM, healthcare revenue cycle compliance, medical billing compliance" />
        <link rel="canonical" href="https://mdercm.com/compliance/" />
        <meta property="og:title" content="HIPAA Compliance & Healthcare Data Security | MDeRCM" />
        <meta property="og:description" content="Fully HIPAA compliant revenue cycle management with ISO 9001 & ISO 27001 certifications." />
        <meta property="og:url" content="https://mdercm.com/compliance/" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        style={{
          background: 'linear-gradient(135deg, #00203F 0%, #002855 50%, #00203F 100%)',
          padding: '120px 5% 80px',
          textAlign: 'center',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Animated Background Elements */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          background: 'radial-gradient(circle at 20% 50%, #4facfe 0%, transparent 50%), radial-gradient(circle at 80% 80%, #e91e63 0%, transparent 50%)'
        }} />

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <div style={{
            display: 'inline-block',
            backgroundColor: 'rgba(255,255,255,0.1)',
            padding: '8px 20px',
            borderRadius: '25px',
            marginBottom: '20px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', letterSpacing: '1px' }}>
              ✓ ISO 9001 & 27001 CERTIFIED
            </span>
          </div>

          <h1 style={{
            fontSize: '56px',
            fontWeight: '700',
            marginBottom: '20px',
            lineHeight: '1.2',
            textShadow: '0 2px 10px rgba(0,0,0,0.3)'
          }}>
            Quality & Compliance
          </h1>
          
          <p style={{
            fontSize: '20px',
            maxWidth: '800px',
            margin: '0 auto 30px',
            lineHeight: '1.6',
            opacity: 0.95,
            fontWeight: '300'
          }}>
            Committed to excellence in healthcare revenue cycle management through rigorous quality standards and comprehensive compliance programs
          </p>

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '30px',
            marginTop: '40px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              padding: '20px 30px',
              borderRadius: '15px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '5px' }}>ISO 9001:2015</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Quality Management</div>
            </div>
            <div style={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              padding: '20px 30px',
              borderRadius: '15px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '5px' }}>HIPAA</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Data Security</div>
            </div>
            <div style={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              padding: '20px 30px',
              borderRadius: '15px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '5px' }}>ISO 27001</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Information Security</div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Tab Navigation */}
      <div style={{
        backgroundColor: '#fff',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 5%',
          display: 'flex',
          gap: '10px',
          overflowX: 'auto'
        }}>
          {[
            { id: 'overview', label: '📋 Overview', icon: '📋' },
            { id: 'iso', label: '🏆 ISO Certification', icon: '🏆' },
            { id: 'hipaa', label: '🔒 HIPAA Compliance', icon: '🔒' },
            { id: 'security', label: '🛡️ Security Standards', icon: '🛡️' },
            { id: 'quality', label: '✨ Quality Assurance', icon: '✨' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '18px 25px',
                backgroundColor: activeTab === tab.id ? '#00203F' : 'transparent',
                color: activeTab === tab.id ? '#fff' : '#666',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #e91e63' : '3px solid transparent',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                fontFamily: "'Poppins', sans-serif"
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Section */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 5%'
      }}>
        
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 style={{
              fontSize: '36px',
              color: '#00203F',
              marginBottom: '20px',
              fontWeight: '700'
            }}>
              Our Commitment to Excellence
            </h2>
            
            <p style={{
              fontSize: '18px',
              color: '#555',
              lineHeight: '1.8',
              marginBottom: '40px'
            }}>
              At MDeRCM Solutions, quality and compliance are not just checkboxes—they're the foundation of everything we do. Our comprehensive quality management system ensures that every client receives consistently excellent service while maintaining the highest standards of data security and regulatory compliance.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '30px',
              marginBottom: '50px'
            }}>
              {[
                {
                  icon: '🎯',
                  title: 'Quality First',
                  description: 'Every process is designed and monitored to deliver superior results for our healthcare partners.'
                },
                {
                  icon: '🔐',
                  title: 'Data Security',
                  description: 'HIPAA-compliant infrastructure with enterprise-grade encryption and access controls.'
                },
                {
                  icon: '📊',
                  title: 'Continuous Improvement',
                  description: 'Regular audits, monitoring, and optimization ensure we exceed industry standards.'
                },
                {
                  icon: '✓',
                  title: 'Certified Excellence',
                  description: 'ISO 9001:2015 certification validates our commitment to quality management systems.'
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  style={{
                    backgroundColor: '#f8f9fa',
                    padding: '35px',
                    borderRadius: '15px',
                    border: '2px solid #e0e0e0',
                    transition: 'all 0.3s ease'
                  }}
                  whileHover={{
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    transform: 'translateY(-5px)'
                  }}
                >
                  <div style={{ fontSize: '48px', marginBottom: '15px' }}>{item.icon}</div>
                  <h3 style={{
                    fontSize: '22px',
                    color: '#00203F',
                    marginBottom: '12px',
                    fontWeight: '700'
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    fontSize: '16px',
                    color: '#666',
                    lineHeight: '1.7'
                  }}>
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Key Compliance Areas */}
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '40px',
              borderRadius: '15px',
              border: '2px solid #2196f3'
            }}>
              <h3 style={{
                fontSize: '28px',
                color: '#00203F',
                marginBottom: '25px',
                fontWeight: '700'
              }}>
                Key Compliance Areas
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px'
              }}>
                {[
                  'HIPAA Privacy & Security Rules',
                  'PHI Protection Standards',
                  'Access Control & Authentication',
                  'Data Encryption (At Rest & In Transit)',
                  'Audit Trails & Monitoring',
                  'Business Associate Agreements',
                  'Incident Response Procedures',
                  'Employee Training & Awareness',
                  'Third-Party Risk Management',
                  'Regular Security Assessments'
                ].map((item, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '15px',
                    color: '#1565c0',
                    fontWeight: '500'
                  }}>
                    <span style={{
                      backgroundColor: '#2196f3',
                      color: 'white',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      flexShrink: 0
                    }}>
                      ✓
                    </span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* ISO Certification Tab */}
        {activeTab === 'iso' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 style={{
              fontSize: '36px',
              color: '#00203F',
              marginBottom: '20px',
              fontWeight: '700'
            }}>
              ISO 9001:2015 Certification
            </h2>

            <p style={{
              fontSize: '18px',
              color: '#555',
              lineHeight: '1.8',
              marginBottom: '40px'
            }}>
              MDeRCM Solutions Private Limited is proud to be ISO 9001:2015 certified, demonstrating our commitment to maintaining the highest quality management standards in healthcare revenue cycle management services.
            </p>

            {/* Certificate Display */}
            <div style={{
              backgroundColor: '#fff',
              padding: '40px',
              borderRadius: '20px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              marginBottom: '40px',
              border: '3px solid #00203F'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '30px',
                marginBottom: '30px'
              }}>
                <div>
                  <h3 style={{
                    fontSize: '28px',
                    color: '#00203F',
                    marginBottom: '10px',
                    fontWeight: '700'
                  }}>
                    Certificate of Registration
                  </h3>
                  <p style={{
                    fontSize: '16px',
                    color: '#666',
                    marginBottom: '5px'
                  }}>
                    <strong>Certified by:</strong> QRO (Quality Research Organization)
                  </p>
                  <p style={{
                    fontSize: '16px',
                    color: '#666',
                    marginBottom: '5px'
                  }}>
                    <strong>Certificate Number:</strong> 305024022104Q
                  </p>
                </div>
                <div style={{
                  backgroundColor: '#e8f5e9',
                  padding: '15px 25px',
                  borderRadius: '10px',
                  border: '2px solid #4caf50'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: '#2e7d32',
                    fontWeight: '600',
                    marginBottom: '5px'
                  }}>
                    ✓ ACTIVE CERTIFICATION
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    Valid until February 20, 2027
                  </div>
                </div>
              </div>

              {/* Certificate Image - Replace with actual PDF/Image */}
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '40px',
                borderRadius: '15px',
                textAlign: 'center',
                border: '2px dashed #dee2e6',
                marginBottom: '30px'
              }}>
                <div style={{
                  fontSize: '80px',
                  marginBottom: '20px'
                }}>
                  🏆
                </div>
                <p style={{
                  fontSize: '18px',
                  color: '#666',
                  marginBottom: '20px'
                }}>
                  ISO 9001:2015 Quality Management System Certificate
                </p>
                <a
                  href="/iso_certificate.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  download="MDeRCM_ISO_9001_Certificate.pdf"
                  style={{
                    display: 'inline-block',
                    padding: '12px 30px',
                    backgroundColor: '#00203F',
                    color: '#fff',
                    textDecoration: 'none',
                    borderRadius: '30px',
                    fontWeight: '600',
                    fontSize: '15px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#e91e63'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#00203F'}
                >
                  📄 View Full Certificate (PDF)
                </a>
              </div>

              {/* Certificate Details */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px'
              }}>
                <div style={{
                  backgroundColor: '#fff3e0',
                  padding: '20px',
                  borderRadius: '10px',
                  border: '2px solid #ff9800'
                }}>
                  <div style={{ fontSize: '14px', color: '#e65100', fontWeight: '600', marginBottom: '5px' }}>
                    Date of Certification
                  </div>
                  <div style={{ fontSize: '18px', color: '#00203F', fontWeight: '700' }}>
                    February 21, 2024
                  </div>
                </div>
                <div style={{
                  backgroundColor: '#e3f2fd',
                  padding: '20px',
                  borderRadius: '10px',
                  border: '2px solid #2196f3'
                }}>
                  <div style={{ fontSize: '14px', color: '#1565c0', fontWeight: '600', marginBottom: '5px' }}>
                    1st Surveillance Audit
                  </div>
                  <div style={{ fontSize: '18px', color: '#00203F', fontWeight: '700' }}>
                    February 20, 2025
                  </div>
                </div>
                <div style={{
                  backgroundColor: '#f3e5f5',
                  padding: '20px',
                  borderRadius: '10px',
                  border: '2px solid #9c27b0'
                }}>
                  <div style={{ fontSize: '14px', color: '#6a1b9a', fontWeight: '600', marginBottom: '5px' }}>
                    2nd Surveillance Audit
                  </div>
                  <div style={{ fontSize: '18px', color: '#00203F', fontWeight: '700' }}>
                    February 20, 2026
                  </div>
                </div>
                <div style={{
                  backgroundColor: '#e8f5e9',
                  padding: '20px',
                  borderRadius: '10px',
                  border: '2px solid #4caf50'
                }}>
                  <div style={{ fontSize: '14px', color: '#2e7d32', fontWeight: '600', marginBottom: '5px' }}>
                    Certificate Expiry
                  </div>
                  <div style={{ fontSize: '18px', color: '#00203F', fontWeight: '700' }}>
                    February 20, 2027
                  </div>
                </div>
              </div>
            </div>

            {/* Scope of Certification */}
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '40px',
              borderRadius: '15px',
              marginBottom: '40px'
            }}>
              <h3 style={{
                fontSize: '24px',
                color: '#00203F',
                marginBottom: '20px',
                fontWeight: '700'
              }}>
                Scope of Certification
              </h3>
              <p style={{
                fontSize: '17px',
                color: '#555',
                lineHeight: '1.8',
                backgroundColor: '#fff',
                padding: '25px',
                borderRadius: '10px',
                border: '2px solid #dee2e6',
                fontStyle: 'italic'
              }}>
                "Providing Revenue Cycle Management Services to USA based hospitals, group of doctors, home healthcare, rehabilitation, and dental clinics."
              </p>
            </div>

            {/* What ISO 9001:2015 Means */}
            <div>
              <h3 style={{
                fontSize: '28px',
                color: '#00203F',
                marginBottom: '25px',
                fontWeight: '700'
              }}>
                What ISO 9001:2015 Certification Means for Our Clients
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '25px'
              }}>
                {[
                  {
                    icon: '✅',
                    title: 'Consistent Quality',
                    description: 'Standardized processes ensure reliable, high-quality service delivery every time.'
                  },
                  {
                    icon: '📈',
                    title: 'Continuous Improvement',
                    description: 'Regular monitoring and optimization of all processes based on data and feedback.'
                  },
                  {
                    icon: '🎯',
                    title: 'Customer Focus',
                    description: 'Systems designed around meeting and exceeding client needs and expectations.'
                  },
                  {
                    icon: '🔍',
                    title: 'Independent Verification',
                    description: 'Third-party audits confirm we meet international quality standards.'
                  },
                  {
                    icon: '⚙️',
                    title: 'Process Excellence',
                    description: 'Documented, tested procedures for every aspect of revenue cycle management.'
                  },
                  {
                    icon: '📊',
                    title: 'Risk Management',
                    description: 'Proactive identification and mitigation of potential quality issues.'
                  }
                ].map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#fff',
                    padding: '30px',
                    borderRadius: '12px',
                    border: '2px solid #e0e0e0',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{ fontSize: '40px', marginBottom: '15px' }}>{item.icon}</div>
                    <h4 style={{
                      fontSize: '18px',
                      color: '#00203F',
                      marginBottom: '10px',
                      fontWeight: '700'
                    }}>
                      {item.title}
                    </h4>
                    <p style={{
                      fontSize: '15px',
                      color: '#666',
                      lineHeight: '1.6'
                    }}>
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* HIPAA Compliance Tab */}
        {activeTab === 'hipaa' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 style={{
              fontSize: '36px',
              color: '#00203F',
              marginBottom: '20px',
              fontWeight: '700'
            }}>
              HIPAA Compliance Program
            </h2>

            <p style={{
              fontSize: '18px',
              color: '#555',
              lineHeight: '1.8',
              marginBottom: '40px'
            }}>
              As a Business Associate handling Protected Health Information (PHI), we maintain comprehensive HIPAA compliance across all operations, ensuring the privacy and security of patient data at every touchpoint.
            </p>

            {/* HIPAA Pillars */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '30px',
              marginBottom: '50px'
            }}>
              {[
                {
                  title: 'Privacy Rule',
                  icon: '🔒',
                  color: '#e91e63',
                  items: [
                    'Minimum necessary access controls',
                    'Patient consent management',
                    'Privacy notice provisions',
                    'Authorization procedures',
                    'Right to access and amendment'
                  ]
                },
                {
                  title: 'Security Rule',
                  icon: '🛡️',
                  color: '#2196f3',
                  items: [
                    'Administrative safeguards',
                    'Physical security measures',
                    'Technical access controls',
                    'Encryption standards',
                    'Audit controls and monitoring'
                  ]
                },
                {
                  title: 'Breach Notification',
                  icon: '⚠️',
                  color: '#ff9800',
                  items: [
                    'Incident detection systems',
                    'Risk assessment protocols',
                    'Notification procedures',
                    'Documentation requirements',
                    'Mitigation strategies'
                  ]
                }
              ].map((pillar, index) => (
                <div key={index} style={{
                  backgroundColor: '#fff',
                  padding: '35px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                  border: `3px solid ${pillar.color}`
                }}>
                  <div style={{
                    fontSize: '50px',
                    marginBottom: '15px',
                    textAlign: 'center'
                  }}>
                    {pillar.icon}
                  </div>
                  <h3 style={{
                    fontSize: '24px',
                    color: pillar.color,
                    marginBottom: '20px',
                    fontWeight: '700',
                    textAlign: 'center'
                  }}>
                    {pillar.title}
                  </h3>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    {pillar.items.map((item, i) => (
                      <li key={i} style={{
                        display: 'flex',
                        alignItems: 'start',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '15px',
                        color: '#666',
                        lineHeight: '1.6'
                      }}>
                        <span style={{
                          color: pillar.color,
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          ✓
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Technical Safeguards */}
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '40px',
              borderRadius: '15px',
              marginBottom: '40px',
              border: '2px solid #2196f3'
            }}>
              <h3 style={{
                fontSize: '28px',
                color: '#00203F',
                marginBottom: '25px',
                fontWeight: '700'
              }}>
                Technical Safeguards Implementation
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px'
              }}>
                {[
                  { label: 'End-to-End Encryption', value: 'AES-256' },
                  { label: 'Multi-Factor Authentication', value: 'Mandatory' },
                  { label: 'Session Timeout', value: '15 Minutes' },
                  { label: 'Password Complexity', value: 'Enforced' },
                  { label: 'Audit Log Retention', value: '7 Years' },
                  { label: 'Intrusion Detection', value: '24/7 Monitoring' },
                  { label: 'Data Backup Frequency', value: 'Real-Time' },
                  { label: 'Disaster Recovery RPO', value: '< 1 Hour' }
                ].map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#fff',
                    padding: '20px',
                    borderRadius: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      {item.label}
                    </span>
                    <span style={{
                      fontSize: '16px',
                      color: '#2196f3',
                      fontWeight: '700'
                    }}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Training Program */}
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '40px',
              borderRadius: '15px'
            }}>
              <h3 style={{
                fontSize: '28px',
                color: '#00203F',
                marginBottom: '20px',
                fontWeight: '700'
              }}>
                HIPAA Training & Awareness Program
              </h3>
              <p style={{
                fontSize: '17px',
                color: '#555',
                lineHeight: '1.8',
                marginBottom: '30px'
              }}>
                Every team member receives comprehensive HIPAA training during onboarding and annually thereafter, ensuring consistent understanding and application of privacy and security requirements.
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px'
              }}>
                {[
                  'New Hire Training',
                  'Annual Refresher Courses',
                  'Policy Updates & Changes',
                  'Incident Response Drills',
                  'Security Awareness Testing',
                  'Role-Specific Training'
                ].map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#fff',
                    padding: '20px',
                    borderRadius: '10px',
                    textAlign: 'center',
                    border: '2px solid #dee2e6',
                    fontSize: '15px',
                    color: '#00203F',
                    fontWeight: '600'
                  }}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Security Standards Tab */}
        {activeTab === 'security' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 style={{
              fontSize: '36px',
              color: '#00203F',
              marginBottom: '20px',
              fontWeight: '700'
            }}>
              Information Security Standards
            </h2>

            <p style={{
              fontSize: '18px',
              color: '#555',
              lineHeight: '1.8',
              marginBottom: '40px'
            }}>
              Our multi-layered security approach protects client data through industry-leading technologies, rigorous policies, and continuous monitoring.
            </p>

            {/* Security Layers */}
            <div style={{
              backgroundColor: '#fff',
              padding: '40px',
              borderRadius: '20px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              marginBottom: '50px'
            }}>
              <h3 style={{
                fontSize: '28px',
                color: '#00203F',
                marginBottom: '30px',
                fontWeight: '700',
                textAlign: 'center'
              }}>
                Security Defense Layers
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}>
                {[
                  {
                    layer: 'Network Security',
                    color: '#e91e63',
                    features: ['Firewall Protection', 'DDoS Mitigation', 'Intrusion Detection/Prevention', 'VPN Access']
                  },
                  {
                    layer: 'Application Security',
                    color: '#9c27b0',
                    features: ['Secure Coding Practices', 'Penetration Testing', 'Vulnerability Scanning', 'Code Reviews']
                  },
                  {
                    layer: 'Data Security',
                    color: '#2196f3',
                    features: ['Encryption at Rest', 'Encryption in Transit', 'Data Loss Prevention', 'Secure Deletion']
                  },
                  {
                    layer: 'Access Control',
                    color: '#4caf50',
                    features: ['Role-Based Access', 'Multi-Factor Auth', 'Privileged Access Management', 'Session Management']
                  },
                  {
                    layer: 'Physical Security',
                    color: '#ff9800',
                    features: ['Secure Data Centers', 'Biometric Access', '24/7 Surveillance', 'Environmental Controls']
                  }
                ].map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#f8f9fa',
                    padding: '25px',
                    borderRadius: '12px',
                    border: `3px solid ${item.color}`,
                    borderLeft: `10px solid ${item.color}`
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '20px'
                    }}>
                      <h4 style={{
                        fontSize: '22px',
                        color: item.color,
                        fontWeight: '700',
                        margin: 0
                      }}>
                        Layer {index + 1}: {item.layer}
                      </h4>
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px'
                      }}>
                        {item.features.map((feature, i) => (
                          <span key={i} style={{
                            backgroundColor: '#fff',
                            padding: '6px 15px',
                            borderRadius: '20px',
                            fontSize: '13px',
                            color: '#666',
                            border: '1px solid #dee2e6',
                            fontWeight: '500'
                          }}>
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SOC 2 Compliance */}
            <div style={{
              backgroundColor: '#f3e5f5',
              padding: '40px',
              borderRadius: '15px',
              marginBottom: '40px',
              border: '3px solid #9c27b0'
            }}>
              <h3 style={{
                fontSize: '28px',
                color: '#00203F',
                marginBottom: '25px',
                fontWeight: '700'
              }}>
                SOC 2 Type II Compliance
              </h3>
              <p style={{
                fontSize: '17px',
                color: '#555',
                lineHeight: '1.8',
                marginBottom: '30px'
              }}>
                Our SOC 2 Type II certification demonstrates our commitment to the five trust service principles over an extended period of operational effectiveness.
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '25px'
              }}>
                {[
                  {
                    principle: 'Security',
                    icon: '🔐',
                    description: 'Protection against unauthorized access, both physical and logical'
                  },
                  {
                    principle: 'Availability',
                    icon: '⚡',
                    description: 'System accessible for operation and use as committed'
                  },
                  {
                    principle: 'Processing Integrity',
                    icon: '✓',
                    description: 'System processing is complete, valid, accurate, and timely'
                  },
                  {
                    principle: 'Confidentiality',
                    icon: '🤐',
                    description: 'Information designated as confidential is protected'
                  },
                  {
                    principle: 'Privacy',
                    icon: '👤',
                    description: 'Personal information collected, used, retained, disclosed properly'
                  }
                ].map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#fff',
                    padding: '25px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
                  }}>
                    <div style={{ fontSize: '40px', marginBottom: '15px', textAlign: 'center' }}>
                      {item.icon}
                    </div>
                    <h4 style={{
                      fontSize: '20px',
                      color: '#9c27b0',
                      marginBottom: '10px',
                      fontWeight: '700',
                      textAlign: 'center'
                    }}>
                      {item.principle}
                    </h4>
                    <p style={{
                      fontSize: '14px',
                      color: '#666',
                      lineHeight: '1.6',
                      textAlign: 'center'
                    }}>
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Security Monitoring */}
            <div style={{
              backgroundColor: '#e8f5e9',
              padding: '40px',
              borderRadius: '15px',
              border: '2px solid #4caf50'
            }}>
              <h3 style={{
                fontSize: '28px',
                color: '#00203F',
                marginBottom: '25px',
                fontWeight: '700'
              }}>
                24/7 Security Monitoring
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px'
              }}>
                {[
                  { metric: 'Uptime SLA', value: '99.9%' },
                  { metric: 'Incident Response Time', value: '< 15 Min' },
                  { metric: 'Security Scans', value: 'Daily' },
                  { metric: 'Log Analysis', value: 'Real-Time' },
                  { metric: 'Threat Intelligence', value: 'Continuous' },
                  { metric: 'Vulnerability Patches', value: '< 24 Hours' }
                ].map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#fff',
                    padding: '25px',
                    borderRadius: '10px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      color: '#666',
                      marginBottom: '8px',
                      fontWeight: '500'
                    }}>
                      {item.metric}
                    </div>
                    <div style={{
                      fontSize: '28px',
                      color: '#4caf50',
                      fontWeight: '700'
                    }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Quality Assurance Tab */}
        {activeTab === 'quality' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 style={{
              fontSize: '36px',
              color: '#00203F',
              marginBottom: '20px',
              fontWeight: '700'
            }}>
              Quality Assurance Program
            </h2>

            <p style={{
              fontSize: '18px',
              color: '#555',
              lineHeight: '1.8',
              marginBottom: '40px'
            }}>
              Our comprehensive quality assurance program ensures every claim, every process, and every interaction meets the highest standards of accuracy and efficiency.
            </p>

            {/* QA Framework */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '30px',
              marginBottom: '50px'
            }}>
              {[
                {
                  title: 'Pre-Submission Review',
                  icon: '🔍',
                  color: '#2196f3',
                  items: [
                    'Automated claim scrubbing',
                    'Coding accuracy verification',
                    'Documentation completeness check',
                    'Payer-specific rules validation'
                  ]
                },
                {
                  title: 'Process Monitoring',
                  icon: '📊',
                  color: '#4caf50',
                  items: [
                    'Real-time KPI tracking',
                    'Statistical process control',
                    'Trend analysis and reporting',
                    'Performance benchmarking'
                  ]
                },
                {
                  title: 'Continuous Improvement',
                  icon: '🔄',
                  color: '#ff9800',
                  items: [
                    'Root cause analysis',
                    'Process optimization',
                    'Staff training & development',
                    'Technology enhancement'
                  ]
                }
              ].map((framework, index) => (
                <div key={index} style={{
                  backgroundColor: '#fff',
                  padding: '35px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                  border: `3px solid ${framework.color}`
                }}>
                  <div style={{
                    fontSize: '50px',
                    marginBottom: '15px',
                    textAlign: 'center'
                  }}>
                    {framework.icon}
                  </div>
                  <h3 style={{
                    fontSize: '24px',
                    color: framework.color,
                    marginBottom: '20px',
                    fontWeight: '700',
                    textAlign: 'center'
                  }}>
                    {framework.title}
                  </h3>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    {framework.items.map((item, i) => (
                      <li key={i} style={{
                        display: 'flex',
                        alignItems: 'start',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '15px',
                        color: '#666',
                        lineHeight: '1.6'
                      }}>
                        <span style={{
                          color: framework.color,
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          ✓
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Quality Metrics */}
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '40px',
              borderRadius: '15px',
              marginBottom: '40px',
              border: '2px solid #2196f3'
            }}>
              <h3 style={{
                fontSize: '28px',
                color: '#00203F',
                marginBottom: '30px',
                fontWeight: '700',
                textAlign: 'center'
              }}>
                Quality Performance Metrics
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '25px'
              }}>
                {[
                  { metric: 'Clean Claims Rate', target: '95%+', current: '97.3%' },
                  { metric: 'Coding Accuracy', target: '98%+', current: '99.1%' },
                  { metric: 'First Pass Resolution', target: '90%+', current: '93.8%' },
                  { metric: 'Denial Rate', target: '<5%', current: '3.2%' },
                  { metric: 'Days in AR', target: '<35', current: '28' },
                  { metric: 'Collection Rate', target: '96%+', current: '97.5%' }
                ].map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#fff',
                    padding: '25px',
                    borderRadius: '12px',
                    textAlign: 'center',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{
                      fontSize: '13px',
                      color: '#666',
                      marginBottom: '8px',
                      fontWeight: '600'
                    }}>
                      {item.metric}
                    </div>
                    <div style={{
                      fontSize: '32px',
                      color: '#4caf50',
                      fontWeight: '700',
                      marginBottom: '5px'
                    }}>
                      {item.current}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#999',
                      fontWeight: '500'
                    }}>
                      Target: {item.target}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Audit Program */}
            <div style={{
              backgroundColor: '#fff3e0',
              padding: '40px',
              borderRadius: '15px',
              border: '2px solid #ff9800'
            }}>
              <h3 style={{
                fontSize: '28px',
                color: '#00203F',
                marginBottom: '20px',
                fontWeight: '700'
              }}>
                Internal Audit Program
              </h3>
              <p style={{
                fontSize: '17px',
                color: '#555',
                lineHeight: '1.8',
                marginBottom: '30px'
              }}>
                Regular internal audits ensure compliance with all quality standards, identify improvement opportunities, and validate the effectiveness of our processes.
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px'
              }}>
                {[
                  { frequency: 'Daily', type: 'Claim Quality Sampling' },
                  { frequency: 'Weekly', type: 'Process Compliance Review' },
                  { frequency: 'Monthly', type: 'Performance Analysis' },
                  { frequency: 'Quarterly', type: 'Comprehensive System Audit' },
                  { frequency: 'Semi-Annual', type: 'External Certification Audit' },
                  { frequency: 'Annual', type: 'Management Review' }
                ].map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#fff',
                    padding: '20px',
                    borderRadius: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '10px'
                  }}>
                    <span style={{
                      fontSize: '18px',
                      color: '#ff9800',
                      fontWeight: '700'
                    }}>
                      {item.frequency}
                    </span>
                    <span style={{
                      fontSize: '14px',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      {item.type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Consultation CTA */}
      <ConsultationCTA />
    </div>
  );
};

export default Compliance;